import { useState, useRef, useEffect } from 'react';
import { User, Crown, Users, ChevronDown } from 'lucide-react';
import { normalizeRoleForDisplay } from '@airclou/utils';
import { shouldShowUserSwitcher } from '../../lib/utils';

export interface MockUser {
  id: string;
  name: string;
  email: string;
  role: string;
  jobTitle?: string;
}

interface HeaderUserSwitcherProps {
  users: MockUser[];
  currentUserId: string;
  onUserChange: (userId: string) => void;
  playbookOwner?: string;
  className?: string;
}

export function HeaderUserSwitcher({ 
  users, 
  currentUserId, 
  onUserChange, 
  playbookOwner,
  className 
}: HeaderUserSwitcherProps) {
  // Don't render user switcher in production
  if (!shouldShowUserSwitcher()) {
    return null;
  }

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const currentUser = users.find(u => u.id === currentUserId);
  const isOwner = currentUser?.name === playbookOwner;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleUserSelect = (userId: string) => {
    onUserChange(userId);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={`relative inline-block text-left ${className || ''}`}>
      <button
        onClick={handleToggle}
        className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-offset-gray-800 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
            <User className="w-3 h-3 text-primary-foreground" />
          </div>
          <span className="hidden sm:block text-sm font-medium">
            {currentUser?.name || 'User'}
          </span>
          {isOwner && (
            <Crown className="w-3 h-3 text-yellow-500" />
          )}
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 origin-top-right bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="py-1">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-foreground">
                  Switch User
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs text-gray-600 dark:text-gray-400">
                {isOwner ? (
                  <>
                    <Crown className="h-3 w-3 text-yellow-500" />
                    <span>Owner (can review drafts)</span>
                  </>
                ) : (
                  <>
                    <Users className="h-3 w-3 text-primary" />
                    <span>Collaborator (can submit drafts)</span>
                  </>
                )}
              </div>
            </div>

            {/* User List */}
            <div className="max-h-96 overflow-y-auto">
              {users.map((user) => {
                const isUserOwner = user.name === playbookOwner;
                const isSelected = user.id === currentUserId;
                
                return (
                  <button
                    key={user.id}
                    onClick={() => handleUserSelect(user.id)}
                    className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                      isSelected
                        ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-[hsl(var(--primary-hover))]'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <div>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {normalizeRoleForDisplay(user.role)}
                          </div>
                        </div>
                      </div>
                      {isUserOwner && (
                        <Crown className="h-4 w-4 text-yellow-500" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Switch users to test different perspectives
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 